/**
 * CancelModal Component
 * Confirmation dialog for subscription cancellation
 */
import { useState } from 'react';
import Button from '@/components/ui/Button';
import { formatDate } from '@/utils/format';

export interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (immediately: boolean, reason?: string) => Promise<void>;
  isLoading?: boolean;
  // WP-BUG-BILLING-SYNC-2: Backend may return null for legacy subscriptions
  periodEnd: string | null;
}

const cancellationReasons = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'not_using', label: 'Not using it enough' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'switching_competitor', label: 'Switching to a competitor' },
  { value: 'temporary', label: 'Only needed temporarily' },
  { value: 'other', label: 'Other reason' },
];

export default function CancelModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  periodEnd,
}: CancelModalProps) {
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const reason = selectedReason === 'other' ? otherReason : selectedReason;
    await onConfirm(cancelImmediately, reason || undefined);
  };

  const handleClose = () => {
    if (!isLoading) {
      setCancelImmediately(false);
      setSelectedReason('');
      setOtherReason('');
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2
                id="cancel-modal-title"
                className="text-lg font-semibold text-gray-900"
              >
                Cancel Subscription
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                We are sorry to see you go. Please let us know how we can improve.
              </p>
            </div>
          </div>

          {/* Cancellation Options */}
          <div className="space-y-4 mb-6">
            {/* When to cancel */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                When should we cancel?
              </label>
              <div className="mt-2 space-y-2">
                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="cancelTiming"
                    checked={!cancelImmediately}
                    onChange={() => setCancelImmediately(false)}
                    className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      At end of billing period
                    </div>
                    <div className="text-sm text-gray-500">
                      Keep access until {formatDate(periodEnd)}
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="cancelTiming"
                    checked={cancelImmediately}
                    onChange={() => setCancelImmediately(true)}
                    className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Cancel immediately
                    </div>
                    <div className="text-sm text-gray-500">
                      Lose access right away (no refund)
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Reason selection */}
            <div>
              <label
                htmlFor="cancel-reason"
                className="text-sm font-medium text-gray-700"
              >
                Reason for canceling (optional)
              </label>
              <select
                id="cancel-reason"
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a reason...</option>
                {cancellationReasons.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Other reason text area */}
            {selectedReason === 'other' && (
              <div>
                <label
                  htmlFor="other-reason"
                  className="text-sm font-medium text-gray-700"
                >
                  Please tell us more
                </label>
                <textarea
                  id="other-reason"
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  rows={3}
                  placeholder="What could we do better?"
                  className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}
          </div>

          {/* Warning */}
          {cancelImmediately && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> Canceling immediately will end your access
                right away. You will not receive a refund for the remaining time in
                your billing period.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Keep Subscription
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              isLoading={isLoading}
            >
              {cancelImmediately ? 'Cancel Now' : 'Cancel at Period End'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
